import React from "react";
import { Crown } from "lucide-react";

/**
 * TierBadge — small visual chip that distinguishes a user's access tier.
 *
 * Tier values:
 *   - "premium" → amber/gold crown (paid premium)
 *   - "trial"   → emerald crown (within 30-day welcome window)
 *   - "free"    → zinc chip WITHOUT icon; only rendered when `showFree` is true.
 *                 Default behaviour hides free-tier badges (Header/Profile/etc)
 *                 but Leaderboard passes showFree so every row has a tier chip.
 *
 * Props:
 *   - tier:      "free" | "trial" | "premium"     (required)
 *   - daysLeft:  number — when set & tier="trial", appends "· X days left"
 *   - size:      "sm" (default) | "xs"
 *   - testid:    optional data-testid override
 *   - showFree:  boolean — when true, renders a "Free" chip for tier="free"
 *                (used on Leaderboard where the spec calls for every user
 *                to show Premium / Trial / Free explicitly).
 */
export default function TierBadge({ tier, daysLeft, size = "sm", testid, fullLabel = false, showFree = false }) {
  if (tier !== "premium" && tier !== "trial" && !(tier === "free" && showFree)) return null;

  const isTrial = tier === "trial";
  const isFree = tier === "free";
  // `fullLabel` is used on the user's OWN status surfaces (Profile page
  // headline, Layout header when self, Settings plan chip) to display the
  // full brand — "Syllox Premium" — instead of the compact "Premium".
  // Community/Leaderboard/Friends keep the compact label to avoid clutter.
  let label;
  if (isFree) label = "Free";
  else if (isTrial) label = fullLabel ? "Trial member" : "Trial";
  else label = fullLabel ? "Syllox Premium" : "Premium";
  const showDays = isTrial && typeof daysLeft === "number" && daysLeft >= 0;

  // Color tokens — zinc for free, emerald for trial, amber for premium.
  let wrapperClass;
  if (isFree) wrapperClass = "bg-zinc-500/15 text-zinc-600 dark:text-zinc-400";
  else if (isTrial) wrapperClass = "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400";
  else wrapperClass = "bg-amber-500/15 text-amber-600 dark:text-amber-400";

  const iconClass = isTrial
    ? "text-emerald-600 dark:text-emerald-400"
    : "text-amber-500 dark:text-amber-400";

  const sizeClasses =
    size === "xs"
      ? "px-1.5 py-0.5 text-[9px]"
      : "px-2 py-0.5 text-[10px]";

  const iconSize = size === "xs" ? 9 : 10;

  const resolvedTestId = testid || `tier-badge-${tier}`;

  return (
    <span
      data-testid={resolvedTestId}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full font-semibold uppercase tracking-wider ${sizeClasses} ${wrapperClass}`}
    >
      {!isFree && <Crown size={iconSize} className={iconClass} />}
      <span>{label}</span>
      {showDays && (
        <span className="normal-case tracking-normal opacity-80">
          {`\u00b7 ${daysLeft}d left`}
        </span>
      )}
    </span>
  );
}
